import { useState, useEffect, createContext, useContext } from "react";
import { SimpleRegistrations } from "components/SimpleRegistrations/SimpleRegistrations";
import { PurposeItem } from "~/core/resources/registrationApi";

// 알림 타입 정의
export type AlertTypeKey = "CUSTOMER_LIST_OVER" | "EXIST_REGISTRATION";

export interface Customer {
  customerId: string;
  name: string;
  visitType: string;
  registrationToday?: boolean;
  birthday?: string;
  sex?: string;
  sessionCount?: number;
}

export interface AlertState {
  open: boolean;
  type: AlertTypeKey | null;
}

export interface SimpleRegistrationContextType {
  isLandscape: boolean;
  loading: boolean;
  setLoading: (loading: boolean) => void;
  showNewCustomerText: null | string;
  setShowNewCustomerText: (show: null | string) => void;
  phoneNumber: string;
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>;
  birthday: string;
  setBirthday: (birthday: string) => void;
  name: string;
  setName: (name: string) => void;
  acquisitionChannelsIds: number[];
  setAcquisitionChannelsIds: (ids: number[]) => void;
  customers: any[];
  setCustomers: (customers: Customer[]) => void;
  smsEnabled: boolean;
  setSmsEnabled: (enabled: boolean) => void;
  eventSmsEnabled: boolean;
  setEventSmsEnabled: (enabled: boolean) => void;
  currentIdx: number;
  setCurrentIdx: (idx: number) => void;
  birthdayCurrentIdx: number;
  setBirthdayCurrentIdx: (idx: number) => void;
  customerId: null | number;
  setCustomerId: (id: null | number) => void;
  visitPurpose: PurposeItem[];
  setVisitPurpose: (purpose: PurposeItem[]) => void;
  page: number;
  setPage: (page: number) => void;
  reset: () => void;

  // 팝업 관련 상태들
  alertState: AlertState;
  setAlertState: (state: AlertState) => void;
  openCustomerListModal: boolean;
  setOpenCustomerListModal: (open: boolean) => void;
  openVisitPurposeModal: boolean;
  setOpenVisitPurposeModal: (open: boolean) => void;
  confirmPhoneNumber: boolean;
  setConfirmPhoneNumber: (confirm: boolean) => void;
  openCreateNewRegistrationModal: boolean;
  setOpenCreateNewRegistrationModal: (open: boolean) => void;
  selectedCustomer: Customer | null;
  setSelectedCustomer: (customer: Customer | null) => void;
}

export const SimpleRegistrationsContext =
  createContext<SimpleRegistrationContextType | null>(null);

export function SimpleRegistrationsPage() {
  const [isLandscape, setIsLandscape] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showNewCustomerText, setShowNewCustomerText] = useState<null | string>(
    null
  );
  const [phoneNumber, setPhoneNumber] = useState("010");
  const [currentIdx, setCurrentIdx] = useState(3);
  const [birthdayCurrentIdx, setBirthdayCurrentIdx] = useState(0);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [birthday, setBirthday] = useState("");
  const [name, setName] = useState("");
  const [acquisitionChannelsIds, setAcquisitionChannelsIds] = useState<
    number[]
  >([]);
  const [visitPurpose, setVisitPurpose] = useState<PurposeItem[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [eventSmsEnabled, setEventSmsEnabled] = useState(false);
  const [page, setPage] = useState(1);
  const [alertState, setAlertState] = useState<AlertState>({
    open: false,
    type: null,
  });
  const [openCustomerListModal, setOpenCustomerListModal] = useState(false);
  const [openVisitPurposeModal, setOpenVisitPurposeModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(
    null
  );
  const [confirmPhoneNumber, setConfirmPhoneNumber] = useState(false);
  const [openCreateNewRegistrationModal, setOpenCreateNewRegistrationModal] =
    useState(false);

  useEffect(() => {
    function handleResize() {
      if (
        window.matchMedia("(orientation: landscape)").matches ||
        window.orientation === 90 ||
        window.orientation === -90
      ) {
        setIsLandscape(true);
      } else {
        setIsLandscape(false);
      }
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const reset = () => {
    setPage(1);
    setBirthdayCurrentIdx(0);
    setCurrentIdx(3);
    setLoading(false);
    setPhoneNumber("010");
    setBirthday("");
    setName("");
    setAcquisitionChannelsIds([]);
    setCustomers([]);
    setSmsEnabled(false);
    setEventSmsEnabled(false);
    setShowNewCustomerText(null);
    setVisitPurpose([]);
    setCustomerId(null);

    // 팝업 상태 초기화
    setAlertState({ open: false, type: null });
    setOpenCustomerListModal(false);
    setOpenVisitPurposeModal(false);
    setSelectedCustomer(null);
    setConfirmPhoneNumber(false);
    setOpenCreateNewRegistrationModal(false);
  };

  return (
    <SimpleRegistrationsContext.Provider
      value={{
        isLandscape,
        loading,
        setLoading,
        showNewCustomerText,
        setShowNewCustomerText,
        phoneNumber,
        setPhoneNumber,
        birthday,
        setBirthday,
        name,
        setName,
        acquisitionChannelsIds,
        setAcquisitionChannelsIds,
        customers,
        setCustomers,
        smsEnabled,
        setSmsEnabled,
        eventSmsEnabled,
        setEventSmsEnabled,
        currentIdx,
        setCurrentIdx,
        birthdayCurrentIdx,
        setBirthdayCurrentIdx,
        visitPurpose,
        setVisitPurpose,
        page,
        setPage,
        reset,
        customerId,
        setCustomerId,

        // 팝업 관련 상태 추가
        alertState,
        setAlertState,
        openCustomerListModal,
        setOpenCustomerListModal,
        openVisitPurposeModal,
        setOpenVisitPurposeModal,
        selectedCustomer,
        setSelectedCustomer,
        confirmPhoneNumber,
        setConfirmPhoneNumber,
        openCreateNewRegistrationModal,
        setOpenCreateNewRegistrationModal,
      }}
    >
      <SimpleRegistrations />
    </SimpleRegistrationsContext.Provider>
  );
}

export const useSimpleRegistrationContext = () => {
  const context = useContext(SimpleRegistrationsContext);
  return context as SimpleRegistrationContextType;
};
